/**
 * Dialog for requesting to borrow an item.
 *
 * Extracted from ItemDetailPanel.tsx to reduce component complexity.
 * Sends a loan request to the appropriate approver (owner or current holder
 * depending on circulation mode).
 */
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useCreateLoanRequest } from '@/hooks/useLoanRequests';
import { useNetworkStatusContext } from '@/contexts/NetworkStatusContext';
import { toast } from 'sonner';
import { T, RADIUS, SHADOW } from '@/lib/tokens';

interface RequestLoanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemId: string;
  itemName: string;
  approverId: string;
  approverLabel: string;
  groupId?: string;
}

/**
 *
 * @param root0
 * @param root0.open
 * @param root0.onOpenChange
 * @param root0.itemId
 * @param root0.itemName
 * @param root0.approverId
 * @param root0.approverLabel
 * @param root0.groupId
 */
export function RequestLoanDialog({
  open,
  onOpenChange,
  itemId,
  itemName,
  approverId,
  approverLabel,
  groupId,
}: RequestLoanDialogProps) {
  const [requestMessage, setRequestMessage] = useState('');
  const createRequest = useCreateLoanRequest();
  const { isOnline } = useNetworkStatusContext();

  const handleSubmit = async () => {
    try {
      await createRequest.mutateAsync({
        itemId,
        approverId,
        message: requestMessage.trim() || undefined,
        groupId,
      });
      toast.success('Loan request sent!');
      setRequestMessage('');
      onOpenChange(false);
    } catch {
      // Error toast handled by useCreateLoanRequest onError
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request to Borrow</DialogTitle>
          <DialogDescription>
            Send a request to borrow "{itemName}" from {approverLabel}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="req-message">Message (optional)</Label>
            <Textarea
              id="req-message"
              placeholder="Add a message to your request..."
              value={requestMessage}
              onChange={(e) => setRequestMessage(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={() => onOpenChange(false)}
            style={{
              padding: '10px 18px',
              borderRadius: RADIUS.md,
              border: `1px solid ${T.border}`,
              background: T.surfaceSubtle,
              color: T.textSec,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={createRequest.isPending || !isOnline}
            title={!isOnline ? "You're offline" : undefined}
            style={{
              padding: '10px 18px',
              borderRadius: RADIUS.md,
              border: 'none',
              background: createRequest.isPending || !isOnline ? T.surfaceSubtle : T.accent,
              color: createRequest.isPending || !isOnline ? T.textMuted : T.white,
              fontSize: 14,
              fontWeight: 600,
              cursor: createRequest.isPending || !isOnline ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              opacity: createRequest.isPending || !isOnline ? 0.7 : 1,
              boxShadow: createRequest.isPending || !isOnline ? 'none' : SHADOW.btn.accent,
            }}
          >
            {createRequest.isPending ? 'Sending...' : 'Send Request'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
